import { Component } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CsvColumnMapper, CsvReader, CsvObjectMapping, CsvObjectMapper } from 'src/app/utils/csv';
import { CustomValidators, FiscalDateDays, Float, stringPropertyIgnoreCase } from 'src/app/utils';
import { Summary } from './models/summary';
import { IPartnerResourceContext, IActivityType, IOrderTask, ICostType, IArticle, IOrderTaskLine, OrderTaskLineService, OrderTaskService } from 'src/app/xena-api';
import { map } from 'rxjs/operators';
import { LocalStorageService } from 'src/app/services';

@Component({
    selector: 'app-import-csv',
    templateUrl: './import-csv.component.html',
    standalone: false
})
export class ImportCsvComponent {
  form: UntypedFormGroup;

  reader = new CsvReader();
  columnMapper = new CsvColumnMapper();
  objectMapper = new CsvObjectMapper();
  savedResourceMappings: Record<string, IPartnerResourceContext> = {};
  savedActivityTypeMappings: Record<string, IActivityType> = {};
  savedOrderTaskMappings: Record<string, IOrderTask> = {};
  savedDefaultCostType: ICostType = null;
  defaultSelecting: UntypedFormGroup;

  columnSettings = {
    hasHeader: true,
    mapping: {
      date: 0,
      employeeId: 1,
      employeeName: 2,
      type: 3,
      description: 4,
      salaryType: 5,
      projectNumber: 6,
      projectName: 7,
      hours: 8,
      quantity: 9,
      articleNumber: 10,
      articleName: 11,
      cost: 12,
      status: 13
    }
  }

  summary: Summary = {
    allLines: 0,
    chosenLines: 0,
    affectedResources: 0,
    affectedActivityTypes: 0,
    affectedOrderTasks: 0,
  };

  currentStep: number = 1;
  open: boolean = true;
  importing: boolean = false;

  lines: Partial<Line>[] = [];

  filteredLines: Partial<Line>[] = [];
  get selecting(): UntypedFormGroup {
    return <UntypedFormGroup>this.form.get('selecting');
  }

  get defaultCostType(): UntypedFormControl {
    return <UntypedFormControl>this.selecting.get('defaultCostType');
  }

  get file(): UntypedFormControl {
    return <UntypedFormControl>this.selecting.get('file');
  }

  get resourceMappings(): UntypedFormArray {
    return <UntypedFormArray>this.form.get('resourceMappings');
  }

  get activityTypeMappings(): UntypedFormArray {
    return <UntypedFormArray>this.form.get('activityTypeMappings');
  }

  get orderTaskMappings(): UntypedFormArray {
    return <UntypedFormArray>this.form.get('orderTaskMappings');
  }

  constructor(
    private fb: UntypedFormBuilder,
    private localStorageService: LocalStorageService,
    private orderTaskLineService: OrderTaskLineService,
    private orderTaskService: OrderTaskService
  ) {
    this.form = this.fb.group({
      selecting: this.fb.group({
        file: [null, Validators.required],
        defaultCostType: [null, Validators.required]
      }),
      resourceMappings: this.fb.array([]),
      activityTypeMappings: this.fb.array([]),
      orderTaskMappings: this.fb.array([])
    });
    this.loadCostType();
  }

  async readLines(): Promise<void> {
    const data = await this.reader.read(this.file.value, { separator: ';', encoding: 'ISO-8859-1' });
    this.lines = this.columnMapper.toObjects<Line>(data, this.columnSettings);
  }

  saveCostType() {
    this.localStorageService.setForFiscal<ICostType>(local_storage_cost_type, this.defaultCostType.value);
  }

  loadCostType() {
    const localStorageResult = this.localStorageService.getForFiscal<ICostType>(local_storage_cost_type);
    if (localStorageResult) {
      this.defaultCostType.setValue(localStorageResult);
    }
  }

  saveResourceMappings() {
    for (let i = 0; i < this.resourceMappings.value.length; i++) {
      this.savedResourceMappings[this.resourceMappings.value[i].employeeId] = this.resourceMappings.value[i].resource;
    }
    this.localStorageService.setForFiscal<Record<string, IPartnerResourceContext>>(local_storage_resource, this.savedResourceMappings);
  }

  loadResourceMappings(): Record<string, IPartnerResourceContext> {
    return this.localStorageService.getForFiscal<Record<string, IPartnerResourceContext>>(local_storage_resource);
  }

  saveActivityTypeMappings() {
    for (let i = 0; i < this.activityTypeMappings.value.length; i++) {
      this.savedActivityTypeMappings[this.activityTypeMappings.value[i].salaryType] = this.activityTypeMappings.value[i].activityType;
    }
    this.localStorageService.setForFiscal<Record<string, IActivityType>>(local_storage_activity_type, this.savedActivityTypeMappings);
  }

  loadActivityTypeMappings(): Record<string, IActivityType> {
    return this.localStorageService.getForFiscal<Record<string, IActivityType>>(local_storage_activity_type);
  }

  saveOrderTaskMappings() {
    for (let i = 0; i < this.orderTaskMappings.value.length; i++) {
      this.savedOrderTaskMappings[this.orderTaskMappings.value[i].projectNumber] = this.orderTaskMappings.value[i].orderTask;
    }
    this.localStorageService.setForFiscal<Record<string, IOrderTask>>(local_storage_order_task, this.savedOrderTaskMappings);
  }

  loadOrderTaskMappings(): Record<string, IOrderTask> {
    return this.localStorageService.getForFiscal<Record<string, IOrderTask>>(local_storage_order_task);
  }

  updateResourceMappings(): void {
    const csvResources = this.lines.reduce((accumulator, currentValue) => {
      if (!accumulator.some(item => item.employeeId === currentValue.employeeId)) {
        accumulator.push(currentValue);
      }
      return accumulator;
    }, []).sort((a, b) => a.employeeId - b.employeeId);

    for (let index = this.resourceMappings.length - 1; index >= 0; index--) {
      const employeeId = this.resourceMappings.at(index).get('employeeId').value;
      if (!csvResources.some(line => line.employeeId === employeeId)) {
        this.resourceMappings.removeAt(index);
      }
    }
    this.savedResourceMappings = this.loadResourceMappings() || {};

    for (let i = 0; i < csvResources.length; i++) {
      if (!this.resourceMappings.value.some(mapping => mapping.employeeId === csvResources[i].employeeId)) {
        this.resourceMappings.insert(i, (this.fb.group({
          selected: true,
          resource: this.savedResourceMappings[csvResources[i].employeeId] || null,
          employeeName: csvResources[i].employeeName,
          employeeId: csvResources[i].employeeId
        }, { validators: CustomValidators.requiredWhenSelected('resource', 'selected') })));

      }
    }
  }

  updateActivityTypeMappings(): void {
    const csvActivityType = this.filteredLines.filter(item => item.salaryType).reduce((accumulator, currentValue) => {
      if (!accumulator.some(item => item.salaryType === currentValue.salaryType)) {
        accumulator.push(currentValue);
      }
      return accumulator;
    }, []).sort(stringPropertyIgnoreCase('salaryType'));

    for (let index = this.activityTypeMappings.length - 1; index >= 0; index--) {
      const salaryType = this.activityTypeMappings.at(index).get('salaryType').value;
      if (!csvActivityType.some(line => line.salaryType === salaryType)) {
        this.activityTypeMappings.removeAt(index);
      }
    }

    this.savedActivityTypeMappings = this.loadActivityTypeMappings() || {};

    for (let i = 0; i < csvActivityType.length; i++) {
      if (!this.activityTypeMappings.value.some(mapping => mapping.salaryType === csvActivityType[i].salaryType)) {
        this.activityTypeMappings.insert(i, (this.fb.group({
          salaryType: csvActivityType[i].salaryType,
          activityType: [this.savedActivityTypeMappings[csvActivityType[i].salaryType] || null, Validators.required]
        })));

      }
    }
  }

  async updateOrderTaskMappings(): Promise<void> {
    const distinctLinesByProjectNumbers = this.filteredLines.reduce((accumulator, currentValue) => {
      if (!accumulator.some(item => item.projectNumber === currentValue.projectNumber)) {
        accumulator.push(currentValue);
      }
      return accumulator;
    }, []).sort(stringPropertyIgnoreCase('projectNumber'));
    this.savedOrderTaskMappings = this.loadOrderTaskMappings() || {};

    const orderTasksNotMapped = distinctLinesByProjectNumbers.filter(pn => !this.savedOrderTaskMappings.hasOwnProperty(pn.projectNumber));

    var matchingOrderTaskNumbers: IOrderTask[] = [];
    const projectNumbersArray = Array.from(new Set(orderTasksNotMapped.map(object => object.projectNumber)));
    if (projectNumbersArray.length > 0) {
      matchingOrderTaskNumbers = await this.orderTaskService.getByNumbers(projectNumbersArray)
        .pipe(map(otn => otn.entities)).toPromise();
    }

    for (const item of matchingOrderTaskNumbers) {
      const orderTaskNumber = item.orderTaskNumber;
      this.savedOrderTaskMappings[orderTaskNumber] = item;
    };

    for (let index = this.orderTaskMappings.length - 1; index >= 0; index--) {
      const projectNumber = this.orderTaskMappings.at(index).get('projectNumber').value;
      if (!distinctLinesByProjectNumbers.some(line => line.projectNumber === projectNumber)) {
        this.orderTaskMappings.removeAt(index);
      }
    };

    for (let i = 0; i < distinctLinesByProjectNumbers.length; i++) {
      if (this.orderTaskMappings.value.some(mapping => mapping.projectNumber === distinctLinesByProjectNumbers[i].projectNumber)) {
        continue;
      }
      this.orderTaskMappings.insert(i, (this.fb.group({
        projectNumber: distinctLinesByProjectNumbers[i].projectNumber,
        projectName: distinctLinesByProjectNumbers[i].projectName,
        orderTask: [this.savedOrderTaskMappings[distinctLinesByProjectNumbers[i].projectNumber] || null, Validators.required]
      })));

      var orderTaskMappingsArray = this.orderTaskMappings.value;
      orderTaskMappingsArray.sort((a, b) => {
        if (a.orderTask === null && b.orderTask === null) {
          return 0;
        } else if (a.orderTask === null) {
          return -1;
        } else if (b.orderTask === null) {
          return 1;
        }
      });
      this.orderTaskMappings.patchValue(orderTaskMappingsArray);
    }
  }

  selectedEmployeeIds(): string[] {
    return this.resourceMappings.value
      .filter(m => m.selected)
      .map(m => m.employeeId)
  }

  filterLines(): void {
    const selectedEmployeeIds = new Set(this.selectedEmployeeIds());
    this.filteredLines = this.lines.filter(l => selectedEmployeeIds.has(l.employeeId));
  }

  updateSummary(): void {
    this.summary.allLines = this.lines.length;
    this.summary.chosenLines = this.filteredLines.length;

    this.summary.affectedResources = this.resourceMappings.value.filter(x => x.selected && x.resource.id !== null).reduce((accumulator, currentValue) => {
      if (!accumulator.some(item => item.resource.id === currentValue.resource.id)) {
        accumulator.push(currentValue);
      }
      return accumulator;
    }, []).length;

    this.summary.affectedActivityTypes = this.activityTypeMappings.value.reduce((accumulator, currentValue) => {
      if (!accumulator.some(item => item.activityType.id === currentValue.activityType.id)) {
        accumulator.push(currentValue);
      }
      return accumulator;
    }, []).length;

    this.summary.affectedOrderTasks = this.orderTaskMappings.value.reduce((accumulator, currentValue) => {
      if (!accumulator.some(item => item.orderTask.id === currentValue.orderTask.id)) {
        accumulator.push(currentValue);
      }
      return accumulator;
    }, []).length;
  }

  createResourceMap(): Map<string, IPartnerResourceContext> {
    return this.resourceMappings.value
      .filter(m => m.selected)
      .reduce((map: Map<string, IPartnerResourceContext>, m) => map.set(m.employeeId, m.resource), new Map<string, IPartnerResourceContext>());
  }

  createActivityTypeMap(): Map<string, IActivityType> {
    return this.activityTypeMappings.value
      .reduce((map: Map<string, IActivityType>, m) => map.set(m.salaryType, m.activityType), new Map<string, IActivityType>());
  }

  createOrderTaskMap(): Map<string, IOrderTask> {
    return this.orderTaskMappings.value
      .reduce((map: Map<string, IOrderTask>, m) => map.set(m.projectNumber, m.orderTask), new Map<string, IOrderTask>());
  }

  createArticleMap(): Map<string, IArticle> {
    return this.filteredLines
      .map(item => item.articleNumber)
      .filter(articleNumber => articleNumber !== '')
      .reduce((map: Map<string, IArticle>, articleNumber) => map.set(articleNumber, null), new Map<string, IArticle>());
  }

  getCostTypeId(): number {
    return this.defaultCostType.value?.id || null;
  }

  createObjectMapping(): CsvObjectMapping<Partial<Line>, IOrderTaskLine> {
    const resourceMap = this.createResourceMap();
    const activityTypeMap = this.createActivityTypeMap();
    const orderTaskMap = this.createOrderTaskMap();
    const articleMap = this.createArticleMap();
    const costTypeId = this.getCostTypeId();

    return {
      fiscalDateDays: line => FiscalDateDays.parseDate(line.date, 'dd-MM-yyyy'),
      orderTaskLineType: line => activityTypeMap.has(line.salaryType) ? 'OrderTaskLineType_Employee' : 'OrderTaskLineType_Default',
      costTypeId: line => activityTypeMap.get(line.salaryType)?.costTypeId || costTypeId,
      resourceId: line => resourceMap.get(line.employeeId)?.id || null,
      activityTypeId: line => activityTypeMap.get(line.salaryType)?.id || null,
      articleId: line => articleMap.get(line.articleNumber)?.id || null,
      description: line => ((activityTypeMap.get(line.salaryType)?.description || articleMap.get(line.articleNumber)?.description || '') + ' ' + line.articleName).trim(),
      orderTaskId: line => orderTaskMap.get(line.projectNumber)?.id || null,
      quantity: line => Float.parse(line.hours, ',') || Float.parse(line.quantity, ','),
      unitId: line => articleMap.get(line.articleNumber)?.salesSetup?.defaultUnitId || null,
      amount: line => (line.cost !== '' ? Float.parse(line.cost, ',') : articleMap.get(line.articleNumber)?.purchasePrice) || 0,
      salesPrice: line => articleMap.get(line.articleNumber)?.salesPrice || 0.00,
      bearerId: line => orderTaskMap.get(line.projectNumber)?.bearerId || null,
      departmentId: line => orderTaskMap.get(line.projectNumber)?.departmentId || null,
      purposeId: line => orderTaskMap.get(line.projectNumber)?.purposeId || null
    };
  }

  async post(orderTaskLines: IOrderTaskLine[]): Promise<void> {
    const groupedOrderTaskLines = orderTaskLines.reduce((grouped: Map<number, IOrderTaskLine[]>, orderTaskLine: IOrderTaskLine) => {
      if (grouped.has(orderTaskLine.resourceId)) {
        grouped.get(orderTaskLine.resourceId).push(orderTaskLine);
      }
      else {
        grouped.set(orderTaskLine.resourceId, [orderTaskLine]);
      }
      return grouped;
    }, new Map<number, IOrderTaskLine[]>());

    for (const line of groupedOrderTaskLines.values()) {
      await this.orderTaskLineService.bulk(line).toPromise();
    }
  }

  //BUTTON CONTROLS
  cancel() {
    this.open = false;
  }

  back() {
    this.currentStep--;
  }

  async next() {
    switch (this.currentStep) {
      case 1:
        if (this.selecting.invalid) {
          this.selecting.markAllAsTouched();
          return;
        }
        if (this.file.dirty) {
          await this.readLines();
          this.updateResourceMappings();
          this.saveCostType();
          this.file.markAsPristine(); // TODO is it posible to detect file change in another way?
        }
        break;
      case 2:
        if (this.resourceMappings.invalid) {
          this.resourceMappings.markAllAsTouched();
          return;
        }
        this.saveResourceMappings();
        this.filterLines();
        this.updateActivityTypeMappings();
        this.updateOrderTaskMappings();
        break;
      case 3:
        if (this.activityTypeMappings.invalid) {
          this.activityTypeMappings.markAllAsTouched();
          return;
        }
        this.saveActivityTypeMappings();

        break;
      case 4:
        if (this.orderTaskMappings.invalid) {
          this.orderTaskMappings.markAllAsTouched();
          return;
        }
        this.saveOrderTaskMappings();
        this.updateSummary();
        break;
      default:
        return;
    }
    this.currentStep++;
  }

  async import() {
    this.importing = true;

    const mapping = this.createObjectMapping();
    const orderTaskLines = this.objectMapper.toObjects<Partial<Line>, IOrderTaskLine>(this.filteredLines, mapping);
    try {
      await this.post(orderTaskLines);
      this.open = false;
    } catch (e) {
      this.importing = false;
    }
  }
}

type Line = {
  date: string,
  employeeName: string,
  employeeId: string,
  salaryType: string,
  projectNumber: string,
  hours: string,
  quantity: string,
  articleNumber: string,
  cost: string,
  description: string,
  articleName: string
};

export const local_storage_cost_type = 'import_csv_costType';
export const local_storage_resource = 'import_csv_resource';
export const local_storage_activity_type = 'import_csv_activityType';
export const local_storage_order_task = 'import_csv_orderTask';




